import type { AgentStatus, TranscriptMessage } from "./types.js";

const SCROLL_THRESHOLD = 40;

export const TRANSCRIPT_CSS = `
  .transcript-panel {
    display: none;
    max-height: 0;
    max-width: 0;
    opacity: 0;
    overflow: hidden;
    transition: max-height 300ms cubic-bezier(0.4, 0, 0.2, 1),
                max-width 0ms 300ms,
                opacity 200ms ease;
    border-radius: 16px;
  }
  .transcript-panel.open {
    display: block;
    max-height: 360px;
    max-width: none;
    opacity: 1;
    transition: max-height 300ms cubic-bezier(0.4, 0, 0.2, 1),
                max-width 0ms,
                opacity 200ms ease;
  }
  .transcript-panel.fab-mode {
    position: absolute;
    bottom: 100%;
    right: 0;
    width: 300px;
    margin-bottom: 8px;
    background: var(--speechify-agent-bg);
    border: 1px solid var(--speechify-agent-border);
    box-shadow: var(--speechify-agent-shadow);
  }
  .transcript-panel.pill-mode {
    margin-top: 8px;
    background: var(--speechify-agent-bg);
    border: 1px solid var(--speechify-agent-border);
    box-shadow: var(--speechify-agent-shadow);
  }
  .transcript-scroll {
    max-height: 320px;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    scrollbar-width: thin;
    scrollbar-color: var(--speechify-agent-muted) transparent;
  }
  .transcript-msg {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .transcript-msg-user { align-items: flex-end; }
  .transcript-msg-assistant { align-items: flex-start; }
  .transcript-role {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--speechify-agent-muted);
  }
  .transcript-bubble {
    max-width: 85%;
    padding: 6px 10px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.4;
    word-break: break-word;
    transition: opacity 200ms ease;
  }
  .transcript-bubble-user {
    background: var(--speechify-agent-accent);
    color: #ffffff;
  }
  .transcript-bubble-assistant {
    background: var(--speechify-agent-border);
    color: var(--speechify-agent-fg);
  }
  .transcript-bubble.interim {
    opacity: 0.6;
    font-style: italic;
  }
  .transcript-thinking {
    display: flex;
    gap: 4px;
    padding: 6px 10px;
    align-self: flex-start;
  }
  .transcript-thinking span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--speechify-agent-muted);
    animation: transcript-pulse 1.2s ease-in-out infinite;
  }
  .transcript-thinking span:nth-child(2) { animation-delay: 0.2s; }
  .transcript-thinking span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes transcript-pulse {
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.4); opacity: 0.4; }
  }

  /* The thinking dots and the panel's expand transition both animate
     indefinitely while a call runs; this panel ships on customer sites, so
     the visitor's motion preference wins over ours. */
  @media (prefers-reduced-motion: reduce) {
    .transcript-panel,
    .transcript-bubble,
    .transcript-thinking span {
      animation: none !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

export class TranscriptPanel {
  #panel: HTMLDivElement;
  #scroll: HTMLDivElement;
  #messagesEl: HTMLDivElement;
  #thinkingEl: HTMLDivElement;
  #renderedCount = 0;
  #userScrolledUp = false;

  constructor(shadowRoot: ShadowRoot) {
    const style = document.createElement("style");
    style.textContent = TRANSCRIPT_CSS;
    shadowRoot.appendChild(style);

    this.#panel = document.createElement("div");
    this.#panel.className = "transcript-panel";

    this.#scroll = document.createElement("div");
    this.#scroll.className = "transcript-scroll";
    this.#scroll.addEventListener("scroll", () => {
      const el = this.#scroll;
      this.#userScrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_THRESHOLD;
    });

    this.#messagesEl = document.createElement("div");
    this.#messagesEl.style.display = "contents";

    this.#thinkingEl = document.createElement("div");
    this.#thinkingEl.className = "transcript-thinking";
    this.#thinkingEl.style.display = "none";
    this.#thinkingEl.innerHTML = "<span></span><span></span><span></span>";

    this.#scroll.appendChild(this.#messagesEl);
    this.#scroll.appendChild(this.#thinkingEl);
    this.#panel.appendChild(this.#scroll);
  }

  get element(): HTMLDivElement {
    return this.#panel;
  }

  setMode(mode: "pill" | "fab"): void {
    this.#panel.classList.toggle("pill-mode", mode === "pill");
    this.#panel.classList.toggle("fab-mode", mode === "fab");
  }

  update(messages: readonly TranscriptMessage[], status: AgentStatus): void {
    for (const msg of messages.slice(this.#renderedCount)) {
      this.#appendMessage(msg);
    }
    this.#renderedCount = Math.max(this.#renderedCount, messages.length);

    const children = this.#messagesEl.children;
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      const bubble = children[i]?.querySelector(".transcript-bubble");
      if (msg && bubble) {
        bubble.textContent = msg.text;
        bubble.classList.toggle("interim", !msg.final);
      }
    }

    const last = messages[messages.length - 1];
    const showThinking =
      (status === "listening" || status === "thinking") && last !== undefined && last.role === "user" && last.final;
    this.#thinkingEl.style.display = showThinking ? "flex" : "none";

    if (!this.#userScrolledUp) {
      this.#scroll.scrollTop = this.#scroll.scrollHeight;
    }
  }

  show(): void {
    this.#panel.classList.add("open");
  }

  hide(): void {
    this.#panel.classList.remove("open");
  }

  clear(): void {
    this.#messagesEl.replaceChildren();
    this.#renderedCount = 0;
    this.#userScrolledUp = false;
    this.#thinkingEl.style.display = "none";
  }

  destroy(): void {
    this.#panel.remove();
  }

  #appendMessage(msg: TranscriptMessage): void {
    const wrapper = document.createElement("div");
    wrapper.className = `transcript-msg transcript-msg-${msg.role}`;

    const role = document.createElement("span");
    role.className = "transcript-role";
    role.textContent = msg.role === "user" ? "You" : "Agent";

    const bubble = document.createElement("span");
    bubble.className = `transcript-bubble transcript-bubble-${msg.role}`;
    if (!msg.final) bubble.classList.add("interim");
    bubble.textContent = msg.text;

    wrapper.append(role, bubble);
    this.#messagesEl.appendChild(wrapper);
  }
}
